import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import useWebSocket, { ReadyState } from "react-use-websocket";

import * as Sentry from "@sentry/react";
import { ulid } from "ulid";
import { UserMessage } from "./components/userMessage";
import { AssistantMessage } from "./components/assistantMessage";

import { useFeatureAccess } from "@/hooks/use-feature-access";
import { type Evidence } from "@/types/evidences";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { StreamMessage, WebSocketMessage } from "./types";
import { useGetAuthenticatedUserQuery } from "@/api/authentication";
import { CopyConversation } from "@/views/chat/components/copyConversation";
import { MessageInput } from "@/views/chat/components/messageInput";
import { usePromptLibraryContext } from "@/modules/promptLibrary/promptLibrary.context";

interface ChatInterfaceProps {
  systemId: string;
  excludeFileIds: string[];
  onCitationClick?: (citation: { documentId: string; citationId: number }) => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ systemId, excludeFileIds }) => {
  const [messages, setMessages] = useState<StreamMessage[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [allEvents, setAllEvents] = useState<WebSocketMessage[]>([]);
  const { subscribe: subscribeToSelectedPrompt, promptToApply, setPromptToApply } = usePromptLibraryContext();

  const userData = useAuthenticatedUser();
  const { hasAccess } = useFeatureAccess();
  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket<WebSocketMessage>("/pf-question-answer/query", {
    shouldReconnect: event => event?.code !== 1000,
    reconnectInterval: 3000,
    skipAssert: true,
    onError: error => {
      console.log("Error:", error);
      Sentry.captureException(error);
    },
  });

  const { refetch } = useGetAuthenticatedUserQuery();

  const isReady = readyState === ReadyState.OPEN;
  const streamingMessageIdRef = useRef<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const processEvent = useCallback((data: WebSocketMessage, newAssistantMessageId: string) => {
    setAllEvents(prevEvents => [...prevEvents, data]);

    switch (data.ev_type) {
      case "query_started": {
        setIsStreaming(true);
        setMessages(prev => [
          ...prev,
          {
            id: newAssistantMessageId,
            content: "",
            isUser: false,
            evidences: [],
          },
        ]);
        streamingMessageIdRef.current = newAssistantMessageId;
        break;
      }
      case "query_token": {
        if (data.t && streamingMessageIdRef.current) {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === streamingMessageIdRef.current ? { ...msg, content: msg.content + data.t } : msg,
            ),
          );
        }
        break;
      }
      case "query_evidence": {
        if (
          !!data.citation_id &&
          data.document_id &&
          data.fse_id &&
          Array.isArray(data.highlights) &&
          streamingMessageIdRef.current
        ) {
          const newEvidence: Evidence = {
            id: data.citation_id,
            citationId: data.citation_id,
            documentId: data.document_id,
            answerOffsetEnd: data.answer_offset_end || 0,
            answerOffsetStart: data.answer_offset_start || 0,
            fseId: data.fse_id,
            highlights: [...data.highlights],
          };
          setMessages(prev =>
            prev.map(msg =>
              msg.id === streamingMessageIdRef.current ? { ...msg, evidences: [...msg.evidences, newEvidence] } : msg,
            ),
          );
        } else {
          console.warn("Invalid evidence data received:", data);
        }
        break;
      }
      case "query_eot": {
        setIsStreaming(false);
        streamingMessageIdRef.current = null;
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (lastJsonMessage !== null) {
      processEvent({ ...lastJsonMessage, t: lastJsonMessage.t || "" }, ulid());
    }
  }, [lastJsonMessage, processEvent]);

  const handleSendMessage = useCallback(
    (message: string) => {
      const newMessage: StreamMessage = {
        id: ulid(),
        content: message,
        isUser: true,
        evidences: [],
      };
      setMessages(prev => [...prev, newMessage]);

      if (isReady) {
        void refetch();
        sendJsonMessage({
          input_type: "query",
          query: message,
          fse_id: systemId,
          exclude_fse_ids: excludeFileIds,
        });
      }
    },
    [isReady, sendJsonMessage, systemId, excludeFileIds, refetch],
  );

  useEffect(() => {
    if (!promptToApply || promptToApply.source !== "qa") {
      return;
    }

    if (!isReady) {
      return;
    }

    handleSendMessage(promptToApply.prompt);
    setPromptToApply(null);
  }, [promptToApply, isReady]);

  useEffect(() => {
    const unsubscribe = subscribeToSelectedPrompt(prompt => handleSendMessage(prompt));

    return () => unsubscribe();
  }, [subscribeToSelectedPrompt, handleSendMessage]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  const handleCopyMessage = useCallback(async (messageId: string, content: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  return (
    <motion.div
      className="flex h-full w-full flex-col rounded-lg"
      initial={false}
      animate={{ marginLeft: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-grow flex-col overflow-hidden bg-gray-100 p-4">
        {hasAccess("system_admin") && (
          <CopyConversation
            isStreaming={isStreaming}
            messages={messages}
            allEvents={allEvents}
            streamingMessageId={streamingMessageIdRef.current}
          />
        )}
        <div ref={chatContainerRef} className="flex-grow overflow-auto">
          {messages.map(message =>
            message.isUser ? (
              <UserMessage
                key={message.id}
                message={message}
                userData={userData}
                onCopyMessage={handleCopyMessage}
                copiedMessageId={copiedMessageId}
              />
            ) : (
              <AssistantMessage
                key={message.id}
                message={message}
                isStreaming={isStreaming && message.id === streamingMessageIdRef.current}
              />
            ),
          )}
        </div>
        <div className="mt-4">
          <MessageInput onSendMessage={handleSendMessage} isStreaming={isStreaming} isReady={isReady} />
        </div>
      </div>
    </motion.div>
  );
};
